import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import environment from '../config/environment';
import { fetchAllowedUsers, isEmailInAllowedList, getUserByEmail } from '../utils/allowedEmails';

export interface User {
  uid: string;
  email: string;
  displayName: string;
  photoURL?: string;
  accessToken: string;
  role: string;
}

export interface AuthContextType {
  currentUser: User | null;
  loginWithGoogle: () => void;
  logout: () => void;
  accessDeniedReason: string | null;
  clearAccessDeniedReason: () => void;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [accessDeniedReason, setAccessDeniedReason] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);

  // Check for access token in URL or existing user in localStorage; enforce server whitelist
  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      const q = hash.startsWith('#') ? hash.slice(1) : hash;
      const accessToken = new URLSearchParams(q).get('access_token');
      if (accessToken) {
        window.history.replaceState({}, document.title, window.location.pathname);
        handleAuthSuccess(accessToken).finally(() => setLoading(false));
        return;
      }
    }

    const savedUser = localStorage.getItem('googleUser');
    if (!savedUser) {
      setLoading(false);
      return;
    }
    let user: User;
    try {
      user = JSON.parse(savedUser);
    } catch (error) {
      console.error('Error parsing saved user:', error);
      localStorage.removeItem('googleUser');
      setLoading(false);
      return;
    }
    fetchAllowedUsers()
      .then((allowedUsers) => {
        if (isEmailInAllowedList(user.email, allowedUsers)) {
          const allowedUser = getUserByEmail(user.email, allowedUsers);
          const role = allowedUser?.role || 'finance';
          setCurrentUser({ ...user, role });
        } else {
          localStorage.removeItem('googleUser');
        }
      })
      .catch(() => setCurrentUser(user))
      .finally(() => setLoading(false));
  }, []);

  const handleAuthSuccess = async (accessToken: string): Promise<void> => {
    try {
      // Verify token with Google
      const response = await fetch(`${environment.google.tokenInfoUrl}?access_token=${accessToken}`);
      if (response.ok) {
        const tokenInfo = await response.json();

        // Get user profile information
        const profileResponse = await fetch('https://www.googleapis.com/oauth2/v2/userinfo', {
          headers: {
            'Authorization': `Bearer ${accessToken}`
          }
        });

        if (profileResponse.ok) {
          const userProfile = await profileResponse.json();
          const allowedUsers = await fetchAllowedUsers();
          if (!isEmailInAllowedList(userProfile.email, allowedUsers)) {
            setAccessDeniedReason('Access Denied! Your email does not have access. Contact your administrator to request access.');
            return;
          }
          setAccessDeniedReason(null);
          const allowedUser = getUserByEmail(userProfile.email, allowedUsers);
          const role = allowedUser?.role || 'finance';
          const user: User = {
            uid: tokenInfo.user_id,
            email: userProfile.email,
            displayName: userProfile.name,
            photoURL: userProfile.picture,
            accessToken: accessToken,
            role
          };

          setCurrentUser(user);
          localStorage.setItem('googleUser', JSON.stringify(user));

          // Generate and log SSO token for testing
          console.log('🔐 USER LOGGED IN - Generating SSO Token for Testing:');
          console.log('👤 User Info:', user);

          // Import token utilities
          import('../utils/tokenUtils').then(({ generateSSOToken }) => {
            generateSSOToken(user).then(token => {
              if (token) {
                console.log('📝 You can manually test by opening the test URL above');
              }
            });
          });
        }
      }
    } catch (error) {
      console.error('Error handling auth success:', error);
    }
  };

  const loginWithGoogle = () => {
    const { google } = environment;
    const redirectUri = window.location.origin;
    const queryParams = new URLSearchParams({
      client_id: google.clientId,
      redirect_uri: redirectUri,
      scope: 'openid profile email',
      response_type: 'token',
      prompt: 'consent',
    });
    console.log('Redirect URI:', redirectUri);
    window.location.href = `${google.loginUrl}?${queryParams}`;
  };

  const logout = () => {
    setCurrentUser(null);
    localStorage.removeItem('googleUser');
    window.location.href = '/';
  };

  const clearAccessDeniedReason = () => setAccessDeniedReason(null);

  // Admin = in admin_emails.json OR has role "admin" (for Rate configure / Email access)
  useEffect(() => {
    if (!currentUser?.email) {
      setIsAdmin(false);
      return;
    }
    const isAdminRole = currentUser.role === 'admin';
    if (isAdminRole) {
      setIsAdmin(true);
      return;
    }
    fetch('/api/admin-emails')
      .then((res) => res.json().catch(() => ({ emails: [] })))
      .then((data: any) => {
        const list = Array.isArray(data?.emails) ? data.emails : [];
        const normalized = (currentUser.email || '').trim().toLowerCase();
        setIsAdmin(!!normalized && list.includes(normalized));
      })
      .catch(() => setIsAdmin(false));
  }, [currentUser?.email, currentUser?.role]);

  const value: AuthContextType = {
    currentUser,
    loginWithGoogle,
    logout,
    accessDeniedReason,
    clearAccessDeniedReason,
    isAdmin
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
